/**
 * The insurance estimate — an Xactimate-shaped document for a claim.
 *
 * ## What this is, and what it is not
 *
 * It is **not** the job's estimate. Profit, commissions, the work order and the
 * contract all keep reading `estimates` through the totals ladder
 * (`ladder.ts`), and nothing here touches any of them. Jonathon, 2026-08-24,
 * asked for a "separate document" and that is exactly what this is: a second
 * sheet of paper, built in the adjuster's own layout, that an adjuster can read
 * line for line against the carrier's own estimate.
 *
 * That separation is the whole safety of the feature. An adjuster-facing
 * document is negotiated — lines get added, argued about and struck out — and
 * none of that should move a number the company runs on.
 *
 * ## The arithmetic, and where it came from
 *
 * Three real Xactimate estimates were worked through by hand on 2026-08-24
 * (`~/Downloads/3-tab pricing.pdf`, `~/Downloads/architecural.pdf` and
 * `~/Downloads/metal.pdf`, all printed under the old entity's branding). Every
 * rule below reproduces those pages, and `test/insuranceEstimate.test.ts` pins
 * the lines that were checked to the cent.
 *
 *   base      = qty × (remove + replace)
 *   tax       = the material share of base, times the sales-tax rate
 *   overhead  = 10% of (base + tax)
 *   profit    = 10% of (base + tax)
 *   total     = base + tax + overhead + profit
 *
 * **Overhead and profit are rounded separately, and that is not a detail.** On
 * the architectural example line 2, 10% of 10,514.05 is 1,051.405 — which
 * rounds to 1,051.41 twice, for an O&P of 2,102.82. Rounding 20% of the same
 * figure in one step gives 2,102.81, and the printed page says 2,102.82. One
 * cent, on the line that decides whether a rep's sheet matches the adjuster's.
 *
 * The summary works the same way: **Overhead on the summary is the sum of the
 * per-line overhead, not 10% of the subtotal.** On the architectural example
 * those are 2,840.38 and 2,840.36 respectively, and the page prints 2,840.38.
 * Summing what is already on the page is also the only version a person can
 * check with a calculator, which matters on a document somebody disputes.
 *
 * ## Tax is estimated here, and cannot be otherwise
 *
 * Xactimate knows the material/labour split of every one of its price-list
 * items and taxes only the material half. We do not have its price list, and
 * the split is not recoverable from a printed line: across the three examples
 * the implied material share runs from about 25% (roofing felt) to about 58%
 * (spaced decking), with no formula joining them.
 *
 * So the rep sets one tax rate for the estimate and a material percentage per
 * line, and this computes the rest. Jonathon chose that on 2026-08-24 over
 * typing every tax figure by hand, knowing it can land a few cents from the
 * carrier's number. `materialPct` is per line precisely so a rep who is trying
 * to match a carrier's sheet exactly can nudge one line until it agrees.
 *
 * ## Where this stops matching, and by how much
 *
 * All 22 lines of `metal.pdf` were checked against this code. **Every base is
 * exact** — line by line, and the 22 sum to the printed Line Item Total of
 * 40,075.89 to the cent. Fifteen of the 22 O&P figures are exact too. The other
 * seven are out by two cents each: one cent on the overhead, one on the profit.
 *
 * They are out because Xactimate rounds its halves from internals this app
 * cannot see. Line 4, the metal eave trim, is the clearest: 157 LF at 6.69 is
 * 1,050.33 exactly and the printed tax is 34.38, so ten percent of the two is
 * 108.471 and rounds to 108.47 — but the page prints 108.48, which needs an O&P
 * base of at least 1,084.75 rather than the 1,084.71 the page itself shows.
 * Something behind that page carries more decimals than it prints, and no
 * rounding rule applied to the printed figures reproduces all 22 lines. Two of
 * them are ties that only work under round-half-up, and two only under
 * round-half-even, so it is not a matter of picking the other mode.
 *
 * The bill for that on a 49,348.88 estimate is two cents. It is recorded here
 * rather than chased, because chasing it means inventing precision we do not
 * have — and a rep who needs one line to agree exactly has the per-line
 * material share to nudge.
 *
 * ## Money
 *
 * Integer cents throughout, per CLAUDE.md rule 3. Prices arrive as whatever the
 * database or a text input holds and go through `toCents` once, here, so no
 * caller has to remember to.
 */

import {
  addCents,
  cents,
  multiplyCents,
  percentOf,
  toCents,
  toPercent,
  type Cents,
  type Percent,
} from "./money.js";

/** Overhead, as a percentage. The industry-standard half of "10 and 10". */
export const DEFAULT_OVERHEAD_PCT = 10 as Percent;

/** Profit, as a percentage. The other half. */
export const DEFAULT_PROFIT_PCT = 10 as Percent;

/**
 * What share of a line is material, when the line does not say.
 *
 * The middle of the range observed across the three reference estimates. It is a
 * starting point to adjust from, not a fact — see the note on tax above.
 */
export const DEFAULT_MATERIAL_PCT = 40;

/**
 * Sales tax, as a percentage, for a Pelham job.
 *
 * A default, and editable on every estimate, because the rate follows the
 * property and not the office. 10 from 2026-10-01 (Jonathon: "fix the
 * insurance too to 10%", ADR 0248); the column default on
 * insurance_estimates.tax_rate says the same number.
 */
export const DEFAULT_SALES_TAX_PCT = 10 as Percent;

/**
 * The units an Xactimate line is measured in.
 *
 * SQ is a roofing square (100 SF); BF is a board foot, which is how framing
 * lumber is priced — `metal.pdf` prices 2x6 rafters that way and nothing on a
 * shingle estimate does. The list is the one that appears across the three
 * reference estimates; it is offered as a dropdown and is not enforced, because
 * an adjuster's line may be written in anything.
 */
export const INSURANCE_UNITS = [
  "SQ",
  "SF",
  "LF",
  "EA",
  "BF",
  "HR",
  "DA",
] as const;

export type InsuranceUnit = (typeof INSURANCE_UNITS)[number];

/**
 * The kinds of roof a claim is written against.
 *
 * Three from the reference estimates, and `custom` for everything else.
 * Choosing one of the three fills the estimate with that roof's usual scope —
 * a tear-off, the covering, its underlayment, its own ridge and edge details —
 * which is the difference between a rep starting from a page and starting from
 * a blank one. `custom` inserts nothing and the rep types their own.
 *
 * Jonathon, 2026-08-24: "there needs to be drop down options based on the pdfs
 * i sent you: 3 Tab, Architectural, Metal. then one for custom and we'll enter
 * those details in."
 *
 * **The stored value is the code, never the label.** `three_tab` is what sits
 * on the row; "3 Tab" is only what a person reads. Reference values on this
 * system are free text on documents and renaming one orphans every row carrying
 * the old string (CLAUDE.md rule 8) — storing the code means the label above it
 * can be reworded whenever somebody likes.
 */
export const INSURANCE_ROOF_TYPES = [
  { code: "three_tab", label: "3 Tab" },
  { code: "architectural", label: "Architectural" },
  { code: "metal", label: "Metal" },
  { code: "custom", label: "Custom" },
] as const;

export type InsuranceRoofType = (typeof INSURANCE_ROOF_TYPES)[number]["code"];

/** The roof types that carry a scope to insert. `custom` deliberately has none. */
export const TEMPLATED_ROOF_TYPES = INSURANCE_ROOF_TYPES.filter(
  (t) => t.code !== "custom",
).map((t) => t.code);

/** What a person reads for a stored roof-type code. */
export function roofTypeLabel(code: string | null | undefined): string {
  return (
    INSURANCE_ROOF_TYPES.find((t) => t.code === code)?.label ??
    INSURANCE_ROOF_TYPES[INSURANCE_ROOF_TYPES.length - 1]!.label
  );
}

/** Does choosing this roof type give the rep any lines? */
export function roofTypeHasTemplate(code: string | null | undefined): boolean {
  return (TEMPLATED_ROOF_TYPES as readonly string[]).includes(code ?? "");
}

/** One typed line. Prices are per unit; `qty` multiplies both of them. */
export interface InsuranceLineInput {
  description: string;
  /**
   * The paragraph under the line — code citations, or the scope of a custom
   * line. Optional, and it is what makes a line an argument rather than an
   * assertion.
   */
  note?: string | null;
  qty?: unknown;
  unit?: string | null;
  /** Per unit. The labour to tear the old one off. */
  removePrice?: unknown;
  /** Per unit. The new material and the labour to fit it. */
  replacePrice?: unknown;
  /**
   * What share of this line is taxable material, 0–100. Null falls back to the
   * estimate's default.
   */
  materialPct?: unknown;
}

/** An area of the property — "Dwelling", "Laundry Room", "Bathroom". */
export interface InsuranceAreaInput {
  name: string;
  items: readonly InsuranceLineInput[];
}

/** The settings that apply to every line on the estimate. */
export interface InsuranceEstimateSettings {
  /** Sales tax rate, as a percentage. */
  taxRate?: unknown;
  /** Material share for lines that do not carry their own, 0–100. */
  defaultMaterialPct?: unknown;
  overheadPct?: unknown;
  profitPct?: unknown;
  /**
   * Whether O&P is claimed at all.
   *
   * Some carriers refuse it, and an estimate that prints it anyway starts the
   * conversation in the wrong place. Off zeroes both halves on every line and
   * on the summary — it does not merely hide them, because a hidden number that
   * still lands in the total is how a document lies.
   */
  applyOverheadAndProfit?: boolean;
}

/** One line, priced. Every figure is a column on the printed page. */
export interface InsuranceLineTotals {
  /** Continuous across areas, starting at 1 — as Xactimate numbers them. */
  number: number;
  description: string;
  note: string | null;
  qty: number;
  unit: string;
  removePrice: Cents;
  replacePrice: Cents;
  materialPct: number;
  /** qty × (remove + replace). The "Line Item Total" column, per line. */
  base: Cents;
  tax: Cents;
  overhead: Cents;
  profit: Cents;
  /** The single O&P column the printed page shows. */
  overheadAndProfit: Cents;
  total: Cents;
}

/** An area, priced, with the totals row that closes it on the page. */
export interface InsuranceAreaTotals {
  name: string;
  items: readonly InsuranceLineTotals[];
  base: Cents;
  tax: Cents;
  overheadAndProfit: Cents;
  total: Cents;
}

/** The whole document, priced. This is what the printed page renders. */
export interface InsuranceEstimateTotals {
  areas: readonly InsuranceAreaTotals[];
  /** The summary page, in the order Xactimate prints it. */
  lineItemTotal: Cents;
  materialSalesTax: Cents;
  subtotal: Cents;
  overhead: Cents;
  profit: Cents;
  replacementCostValue: Cents;
  /**
   * What the carrier is being asked for.
   *
   * Equal to RCV today: there is no depreciation on this document, which is
   * where Jonathon left it on 2026-08-24 and matches all three reference estimates
   * (their RCV and Net Claim are the same figure). It is a separate field
   * rather than an alias so that adding depreciation later changes one
   * calculation instead of every caller.
   */
  netClaim: Cents;
}

/** Clamp a percentage to 0–100. A material share outside that is a typo. */
const materialShare = (value: unknown, fallback: number): number => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = toPercent(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, parsed));
};

const quantity = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : Number.parseFloat(String(value ?? "").replace(/[,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Price one line.
 *
 * Exported on its own because the editor prices a row as it is typed, before
 * there is an estimate to put it on.
 */
export function computeInsuranceLine(
  line: InsuranceLineInput,
  settings: InsuranceEstimateSettings = {},
): Omit<InsuranceLineTotals, "number"> {
  const taxRate = toPercent(settings.taxRate ?? DEFAULT_SALES_TAX_PCT);
  const defaultMaterial = materialShare(
    settings.defaultMaterialPct,
    DEFAULT_MATERIAL_PCT,
  );
  const applyOP = settings.applyOverheadAndProfit !== false;
  const overheadPct = applyOP
    ? toPercent(settings.overheadPct ?? DEFAULT_OVERHEAD_PCT)
    : (0 as Percent);
  const profitPct = applyOP
    ? toPercent(settings.profitPct ?? DEFAULT_PROFIT_PCT)
    : (0 as Percent);

  const qty = quantity(line.qty);
  const removePrice = toCents(line.removePrice);
  const replacePrice = toCents(line.replacePrice);
  const materialPct = materialShare(line.materialPct, defaultMaterial);

  // One multiplication, not two. Rounding remove and replace separately and
  // adding them can differ by a cent from the way the source system prints it,
  // which shows the two halves but totals the whole.
  const unitPrice = addCents(removePrice, replacePrice);
  const base = multiplyCents(unitPrice, qty);

  const taxableMaterial = percentOf(base, materialPct as Percent);
  const tax = percentOf(taxableMaterial, taxRate);

  // The base O&P is charged on, and the reason tax is computed first: the
  // reference estimates apply O&P to the taxed figure, not the bare line.
  const opBase = addCents(base, tax);
  // Rounded separately — see the header. This is worth a cent per line.
  const overhead = percentOf(opBase, overheadPct);
  const profit = percentOf(opBase, profitPct);

  return {
    description: line.description ?? "",
    note: line.note?.trim() ? line.note : null,
    qty,
    unit: line.unit ?? "",
    removePrice,
    replacePrice,
    materialPct,
    base,
    tax,
    overhead,
    profit,
    overheadAndProfit: addCents(overhead, profit),
    total: addCents(base, tax, overhead, profit),
  };
}

/**
 * Price the whole document.
 *
 * Line numbers run continuously across areas, which is how Xactimate numbers
 * them and how an adjuster refers to one on the phone ("strike 14").
 */
export function computeInsuranceEstimate(
  areas: readonly InsuranceAreaInput[],
  settings: InsuranceEstimateSettings = {},
): InsuranceEstimateTotals {
  let number = 0;

  const priced: InsuranceAreaTotals[] = areas.map((area) => {
    const items = area.items.map((item) => {
      number += 1;
      return { number, ...computeInsuranceLine(item, settings) };
    });

    return {
      name: area.name,
      items,
      base: cents(items.reduce((sum, i) => sum + i.base, 0)),
      tax: cents(items.reduce((sum, i) => sum + i.tax, 0)),
      overheadAndProfit: cents(
        items.reduce((sum, i) => sum + i.overheadAndProfit, 0),
      ),
      total: cents(items.reduce((sum, i) => sum + i.total, 0)),
    };
  });

  const every = priced.flatMap((a) => a.items);
  const lineItemTotal = cents(every.reduce((sum, i) => sum + i.base, 0));
  const materialSalesTax = cents(every.reduce((sum, i) => sum + i.tax, 0));
  const subtotal = addCents(lineItemTotal, materialSalesTax);
  // Summed from the lines, not recomputed from the subtotal. See the header.
  const overhead = cents(every.reduce((sum, i) => sum + i.overhead, 0));
  const profit = cents(every.reduce((sum, i) => sum + i.profit, 0));
  const replacementCostValue = addCents(subtotal, overhead, profit);

  return {
    areas: priced,
    lineItemTotal,
    materialSalesTax,
    subtotal,
    overhead,
    profit,
    replacementCostValue,
    netClaim: replacementCostValue,
  };
}
